import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Divider,
  FormControlLabel, IconButton, Paper, Rating, Stack, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { ListSearchField, useSearchableRows } from "@/components/ListSearchField";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  vendorItemsApi, type VendorItemDto, type VendorItemPayload,
  type VendorDto, type ItemDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ItemPicker, VendorPicker } from "@/components/Pickers";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";

export function VendorItemsPage() {
  const [mode, setMode] = useState<"vendor" | "item">("vendor");

  return (
    <Box>
      <PageHeader breadcrumbs={["Catalog"]} title="Vendor items & grades"
        actions={<><ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="vendor">Manage by vendor</ToggleButton>
          <ToggleButton value="item">Suggest by item</ToggleButton>
        </ToggleButtonGroup></>}
      />

      {mode === "vendor" ? <ManageByVendor /> : <SuggestByItem />}
    </Box>
  );
}

// =============================== Manage by vendor ===============================
function ManageByVendor() {
  const qc = useQueryClient();
  const [vendor, setVendor] = useState<VendorDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<VendorItemDto | null>(null);
  const canManage = useAuth(s => s.hasPermission("procurement.vendoritem.manage"));

  const query = useQuery({
    queryKey: ["vendor-items", "by-vendor", vendor?.id],
    queryFn: () => vendorItemsApi.list({ vendorId: vendor!.id, pageSize: 500 }),
    enabled: !!vendor,
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Vendor item removed" },
    mutationFn: (id: string) => vendorItemsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor-items"] }),
  });

  const { search, setSearch, rows } = useSearchableRows(
    query.data?.items ?? [], r => `${r.itemSku} ${r.itemName}`);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Box sx={{ minWidth: 320, flex: 1 }}>
            <VendorPicker value={vendor} onChange={setVendor} label="Vendor" />
          </Box>
          {canManage && vendor && (
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setEditing(null); setDialogOpen(true); }}>
              Add item
            </Button>
          )}
        </Stack>
        {vendor && (
          <>
            <Divider sx={{ my: 2 }} />
            <VendorSummary vendor={vendor} />
          </>
        )}
      </Paper>

      {!vendor && <Alert severity="info">Pick a vendor to view and grade the items they supply.</Alert>}
      {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
      {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}

      {vendor && (
        <Paper>
          <Box sx={{ p: 2, pb: 0 }}>
            <ListSearchField value={search} onChange={setSearch}
              placeholder="Search by item SKU or name" fullWidth />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Last rate</TableCell>
                  <TableCell align="right">Lead (d)</TableCell>
                  <TableCell align="center">Rate</TableCell>
                  <TableCell align="center">Speed</TableCell>
                  <TableCell align="center">Quality</TableCell>
                  <TableCell align="center">Overall</TableCell>
                  <TableCell align="center">Status</TableCell>
                  {canManage && <TableCell align="right" width={96}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      {r.isPreferred && <StarIcon fontSize="inherit" color="warning" sx={{ verticalAlign: "middle", mr: 0.5 }} />}
                      <b>{r.itemSku}</b> · {r.itemName}
                    </TableCell>
                    <TableCell align="right">{r.lastRate ?? "—"}</TableCell>
                    <TableCell align="right">{r.leadTimeDays ?? "—"}</TableCell>
                    <TableCell align="center"><Rating size="small" readOnly value={r.rateScore} /></TableCell>
                    <TableCell align="center"><Rating size="small" readOnly value={r.deliverySpeedScore} /></TableCell>
                    <TableCell align="center"><Rating size="small" readOnly value={r.deliveryQualityScore} /></TableCell>
                    <TableCell align="center"><Chip size="small" label={r.overallScore.toFixed(1)} /></TableCell>
                    <TableCell align="center">
                      <Chip size="small" color={r.isActive ? "success" : "default"}
                        label={r.isActive ? "Active" : "Inactive"} />
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small"
                          onClick={() => { setEditing(r); setDialogOpen(true); }}>
                          <EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Remove"><IconButton size="small" color="error"
                          onClick={() => { if (confirm(`Remove ${r.itemName} from this vendor?`)) deleteMutation.mutate(r.id); }}>
                          <DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {rows.length === 0 && !query.isLoading && (
                  <TableRow><TableCell colSpan={9}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      {search
                        ? `No item matches "${search}".`
                        : "No items linked to this vendor yet."}
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {vendor && (
        <VendorItemDialog
          open={dialogOpen} onClose={() => setDialogOpen(false)}
          vendor={vendor} row={editing}
        />
      )}
    </Box>
  );
}

// =============================== Vendor identity block ===============================
interface VendorAddress {
  line1?: string; line2?: string; city?: string;
  state?: string; pincode?: string; country?: string;
}

/** Same serialised-address shape the vendor form writes. */
function formatAddress(json?: string | null): string {
  if (!json) return "";
  let a: VendorAddress;
  try { a = JSON.parse(json) as VendorAddress; } catch { return ""; }
  return [a.line1, a.line2, a.city, a.state, a.pincode, a.country]
    .map(p => p?.trim()).filter(Boolean).join(", ");
}

function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? "monospace" : undefined }}>{value || "—"}</Typography>
    </Stack>
  );
}

/**
 * Identity block for the selected vendor, mirroring <PatientSummary>: once a vendor is picked the
 * screen states who it is, not just which row is highlighted in the dropdown. `dense` trims it to a
 * single line for the dialog header.
 */
function VendorSummary({ vendor, dense = false }: { vendor: VendorDto; dense?: boolean }) {
  const address = formatAddress(vendor.addressJson);
  const credit = [vendor.paymentTerms, vendor.creditDays != null ? `${vendor.creditDays} days` : null]
    .filter(Boolean).join(" · ");

  if (dense) {
    return (
      <Typography variant="body2" color="text.secondary">
        <b>{vendor.code}</b> · {vendor.name}
        {vendor.gstin ? ` · GSTIN ${vendor.gstin}` : ""}
        {vendor.phone ? ` · ${vendor.phone}` : ""}
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>{vendor.name}</Typography>
        <Chip size="small" variant="outlined" label={vendor.code} sx={{ fontFamily: "monospace" }} />
        <StatusPill label={vendor.isActive ? "Active" : "Inactive"} />
        {vendor.supplyTypeName && <Chip size="small" label={vendor.supplyTypeName} />}
      </Stack>
      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
        <Field label="GSTIN" value={vendor.gstin} mono />
        <Field label="PAN" value={vendor.pan} mono />
        <Field label="Contact person" value={vendor.contactPerson} />
        <Field label="Phone" value={vendor.phone} />
        <Field label="Email" value={vendor.email} />
        <Field label="Payment terms" value={credit} />
        <Field label="Payable account"
          value={vendor.accountCode ? `${vendor.accountCode} — ${vendor.accountName ?? ""}`.trim() : null} />
        {address && <Field label="Address" value={address} />}
      </Stack>
    </Box>
  );
}

// =============================== Add / edit dialog ===============================
function VendorItemDialog({
  open, onClose, vendor, row,
}: { open: boolean; onClose: () => void; vendor: VendorDto; row: VendorItemDto | null }) {
  const qc = useQueryClient();
  const vendorId = vendor.id;
  const isEdit = !!row;
  const [item, setItem] = useState<ItemDto | null>(null);
  const [form, setForm] = useState<VendorItemPayload>({
    vendorId, rateScore: 0, deliverySpeedScore: 0, deliveryQualityScore: 0, isPreferred: false, isActive: true,
  });

  useEffect(() => {
    if (!open) return;
    setItem(null);
    setForm(row
      ? {
          vendorId, itemId: row.itemId,
          lastRate: row.lastRate ?? null, leadTimeDays: row.leadTimeDays ?? null, vendorSku: row.vendorSku ?? null,
          rateScore: row.rateScore, deliverySpeedScore: row.deliverySpeedScore,
          deliveryQualityScore: row.deliveryQualityScore, isPreferred: row.isPreferred, isActive: row.isActive,
        }
      : { vendorId, rateScore: 0, deliverySpeedScore: 0, deliveryQualityScore: 0, isPreferred: false, isActive: true });
  }, [open, row, vendorId]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Vendor item updated" : "Item linked to vendor" },
    mutationFn: async () => {
      const payload: VendorItemPayload = { ...form, vendorId, itemId: isEdit ? row!.itemId : item?.id };
      if (isEdit && row) return vendorItemsApi.update(row.id, payload);
      return vendorItemsApi.create(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-items"] }); onClose(); },
  });

  const canSubmit = isEdit || !!item;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit vendor item" : "Link item to vendor"}</ClosableDialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}><VendorSummary vendor={vendor} dense /></Box>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Stack spacing={2}>
          {isEdit
            ? <TextField label="Item" size="small" fullWidth disabled value={`${row!.itemSku} · ${row!.itemName}`} />
            : <ItemPicker value={item} onChange={setItem} label="Item" required />}

          <Stack direction="row" spacing={2}>
            <TextField label="Last rate" type="number" size="small" fullWidth
              value={form.lastRate ?? ""} inputProps={{ min: 0, step: "any" }}
              onChange={e => setForm(f => ({ ...f, lastRate: e.target.value === "" ? null : Number(e.target.value) }))} />
            <TextField label="Lead time (days)" type="number" size="small" fullWidth
              value={form.leadTimeDays ?? ""} inputProps={{ min: 0 }}
              onChange={e => setForm(f => ({ ...f, leadTimeDays: e.target.value === "" ? null : Number(e.target.value) }))} />
          </Stack>

          <TextField label="Vendor SKU (optional)" size="small" fullWidth
            value={form.vendorSku ?? ""}
            onChange={e => setForm(f => ({ ...f, vendorSku: e.target.value || null }))} />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Grades (0 = ungraded)</Typography>
            <Stack spacing={1}>
              <GradeRow label="Rate / price" value={form.rateScore} onChange={v => setForm(f => ({ ...f, rateScore: v }))} />
              <GradeRow label="Delivery speed" value={form.deliverySpeedScore} onChange={v => setForm(f => ({ ...f, deliverySpeedScore: v }))} />
              <GradeRow label="Delivery quality" value={form.deliveryQualityScore} onChange={v => setForm(f => ({ ...f, deliveryQualityScore: v }))} />
            </Stack>
          </Box>

          <Stack direction="row" spacing={3}>
            <FormControlLabel label="Preferred vendor"
              control={<Switch checked={form.isPreferred} onChange={(_, v) => setForm(f => ({ ...f, isPreferred: v }))} />} />
            {isEdit && (
              <FormControlLabel label="Active"
                control={<Switch checked={form.isActive ?? true} onChange={(_, v) => setForm(f => ({ ...f, isActive: v }))} />} />
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => mutation.mutate()}>
          {isEdit ? "Save" : "Link"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GradeRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="body2">{label}</Typography>
      <Rating value={value} onChange={(_, v) => onChange(v ?? 0)} />
    </Stack>
  );
}

// =============================== Suggest by item ===============================
function SuggestByItem() {
  const [item, setItem] = useState<ItemDto | null>(null);
  const query = useQuery({
    queryKey: ["vendor-items", "suggest", item?.id],
    queryFn: () => vendorItemsApi.suggest(item!.id),
    enabled: !!item,
  });
  const rows = query.data ?? [];

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ maxWidth: 480 }}>
          <ItemPicker value={item} onChange={setItem} label="Item to source" />
        </Box>
      </Paper>

      {!item && <Alert severity="info">Pick an item to see its vendors ranked by grade (preferred → overall → lowest rate).</Alert>}
      {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}

      {item && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={60}>Rank</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell align="center">Overall</TableCell>
                  <TableCell align="center">Rate</TableCell>
                  <TableCell align="center">Speed</TableCell>
                  <TableCell align="center">Quality</TableCell>
                  <TableCell align="right">Last rate</TableCell>
                  <TableCell align="right">Lead (d)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={r.id} hover selected={idx === 0}>
                    <TableCell>
                      {idx === 0 ? <Chip size="small" color="success" label="Best" /> : idx + 1}
                    </TableCell>
                    <TableCell>
                      {r.isPreferred && <StarIcon fontSize="inherit" color="warning" sx={{ verticalAlign: "middle", mr: 0.5 }} />}
                      {r.vendorName}
                    </TableCell>
                    <TableCell align="center"><Chip size="small" label={r.overallScore.toFixed(1)} /></TableCell>
                    <TableCell align="center">{r.rateScore || "—"}</TableCell>
                    <TableCell align="center">{r.deliverySpeedScore || "—"}</TableCell>
                    <TableCell align="center">{r.deliveryQualityScore || "—"}</TableCell>
                    <TableCell align="right">{r.lastRate ?? "—"}</TableCell>
                    <TableCell align="right">{r.leadTimeDays ?? "—"}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && !query.isLoading && (
                  <TableRow><TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      No graded vendors supply this item yet.
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
